import PageTemplate from "../classes/PageTemplate";

import NetworkManager from "./NetworkManager";
import ShortPage from "../classes/ShortPage";
import Tag from "../classes/Tag";

const TOPIC = "grievances";

const SECTION_IDS = {
    Reasons: "reasons",
    Possibilities: "possibilities"
}

const SECTIONS = new Map([
    ["reasons", "Warum die Energiewende scheitert..."],
    ["possibilities", "...und was wir tun können!"]
]);

const SECTION_PARTS = {
    "problem": {
        "sections": [SECTION_IDS.Reasons],
        "title": "Problem",
        "template": ""
    },
    "statement": {
        "sections": [SECTION_IDS.Reasons],
        "title": "Stellungnahme Projektant",
        "template": ""
    },
    "opposing": {
        "sections": [SECTION_IDS.Possibilities],
        "title": "Gegenposition",
        "template": ""
    },
    "approach": {
        "sections": [SECTION_IDS.Possibilities, SECTION_IDS.Reasons],
        "title": "Handlungsoptionen",
        "template": ""
    }
};

/** @type {[string, number[]][]} */
const TAG_CATEGORIES = [
    [
        "Strom",
        [0, 1, 2, 3, 4, 5, 6, 7, 8]
    ],
    [
        "Mobilität",
        [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21]
    ],
    [
        "Politisch / Regulierung/ Förderung",
        [22,23,24,25,26,27,28,29,30,31,32,33,34,35]
    ],
    [
        "Effizienzmaßnahmen und Gebäudetechnik",
        [36,37,38,39,40,41,42,43,44,45,46,47,48,49,50]
    ],
    [
        "Technologien und Verfahren",
        [51,52,53,54,55,56,57,58,59,60,61,62,63,64,65,66,67,68,69,70,71,72,73,74,75,76,77,78,79,80,81]
    ],
    [
        "Akzeptanz / Partizipation",
        [82,83,84,85,86,87,88,89,90,91,92]
    ],
    [
        "Umweltwechselwirkungen",
        [93,94,95,96,97,98,99,100,101,102,103,104,105,106]
    ]
]

const networkManager = NetworkManager.instance;

/** @typedef {{ id: string, title: string, template: string, sections: string[] }} SectionPartObj */
/** @typedef {{ id: string: string, tags: [number, string][], audiences: [number, string][] }} TopicInfoObj */
/** @typedef {import("../classes/PageTemplate").PageTemplateObj} PageTemplateObj */

export default class PageManager {

    get TOPIC() { return TOPIC; }

    get SECTIONS() { return SECTIONS; }

    get SECTION_IDS() { return SECTION_IDS; }

    get TAG_CATEGORIES() { return TAG_CATEGORIES; }

    constructor() {
        /** @type {ShortPage[]} */
        this.shortPages = [];

        /** @type {Map<number, Tag>} */
        this.tags = new Map();

        /** @type {Map<number, Tag>} */
        this.audiences = new Map();

        /** @type {Map<string, SectionPartObj>} */
        this.sectionParts = new Map( Object.entries(SECTION_PARTS).map( ({ 0: id, 1: sectionPart }) => { sectionPart.id = id; return [id, sectionPart]; } ) );
        this.fetchedShortPages = false;
    }

    /**
     * @returns {PageManager}
     */
    static get instance() {
        if (PageManager._instance === undefined) {
            PageManager._instance = new PageManager();
        }
        
        return PageManager._instance;
    }

    newPageTemplate() {
        return new PageTemplate(
            TOPIC,
            "",
            Array.from(this.sectionParts).map( kv => { kv[1] = kv[1].template; return kv; } )
        );
    }

    hasFetchedShortPages() {
        return this.fetchedShortPages;
    }

    getSelectedTags() {
        
        return this.getTagsArray().filter( tag => tag.selected );
    }

    getSelectedAudiences() {
        return this.getAudiencesArray().filter( tag => tag.selected );
    }

    getShortPagesWithSelectedTags() {
        const selectedTags = this.getSelectedTags();
        return this.shortPages.filter( page => !selectedTags.some( selectedTag => page.tagIds.findIndex( tagId => tagId === selectedTag.id ) === -1 ) );
    }

    getShortPagesWithSelectedAudiences() {
        const selectedTags = this.getSelectedAudiences();
        return this.shortPages.filter( page => !selectedTags.some( selectedTag => page.audienceIds.findIndex( tagId => tagId === selectedTag.id ) === -1 ) );
    }

    getShortPages() {
        return this.shortPages;
    }

    getTags() {
        return this.tags;
    }

    getAudiences() {
        return this.audiences;
    }

    getSectionsArray() {
        return Array.from( this.sectionParts.values() );
    }

    /**
     * @param {string} id
     */
    getSectionPart( id ) {
        return this.sectionParts.get(id);
    }

    /**
     * @param {string} sectionName
     */
    getSectionPartsBySectionName( sectionName ) {
        return this.getSectionsArray().filter( sectionPart => sectionPart.sections.some( section => section === sectionName ) );
    }

    getTagsArray() {
        return Array.from( this.tags.values() );
    }

    getGroupedTags() {
        return TAG_CATEGORIES.map( tagCategory => ({
                name: tagCategory[0],
                tags: tagCategory[1].map( tagId => this.getTag(tagId) )
            })
        );
    }

    getAudiencesArray() {
        return Array.from( this.audiences.values() );
    }

    /**
     * @param {number} id 
     */
    getTag( id ) {
        return this.tags.get( id );
    }

    /**
     * @param {number} id 
     */
    getAudience( id ) {
        return this.audiences.get( id );
    }

    async refetchTopicInfo() {

        try {

            /** @type {TopicInfoObj} */
            const topicInfo = await networkManager.fetch( "/topicInfo", "GET", { topic: TOPIC } );
            this.tags = new Map( topicInfo.tags.sort( (a, b) => a[1].localeCompare(b[1]) ).map( tag => [tag[0], new Tag(...tag)] ) );
            this.audiences = new Map( topicInfo.audiences.sort( (a, b) => a[1].localeCompare(b[1]) ).map( tag => [tag[0], new Tag(...tag)] ) );
            console.info(`Fetched tags "${topicInfo.tags.map(t => t[1]).join(", ")}" and audiences "${topicInfo.audiences.map(t => t[1]).join(", ")}" of topic "${TOPIC}"`);

        } catch(e) {
            console.error(`Could not fetch tags of topic "${TOPIC}"`, e);
            return false;
        }

        // success
        return true;

    }

    async refetchShortPages() {

        console.info(`Refetching short pages of topic "${TOPIC}"`);

        try {

            this.shortPages = (await networkManager.fetch( "/allShortEntries", "GET", { topic: TOPIC } )).map( shortPageObj => ShortPage.fromObject(shortPageObj) );

        } catch(e) {
            console.error(`Could not refetch pages with topic "${TOPIC}"`, e);
            return false;
        }

        console.log(`Fetched ${this.shortPages.length} pages`);

        // success
        this.fetchedShortPages = true;
        return true;

    }

    /**
     * Converts the html parts of the page to one with the given headers
     * @param {PageTemplate} page The page to convert
     */
    pageTemplateToHtml( page ) {
        return Array.from( this.sectionParts.keys() )
            .filter( partId => page.text.get(partId) )
            .map( partId => `<h3>${this.getSectionPart(partId).title}:</h3>${page.text.get(partId)}` )
            .join("<br/>");
    }

    /**
     * @param {PageTemplateObj} pageObj 
     */
    async uploadPage( pageObj ) {
        // set partition key
        pageObj.topic = TOPIC;

        // upload
        console.log("Uploading page:", pageObj);
        const page = PageTemplate.fromObject( await networkManager.fetch( pageObj.id ? "/forumEntry" : "/uploadForumEntry", "POST", undefined, pageObj ) );
        page.setTags( page.tagIds.map( tagId => this.getTag( tagId ) ) );
        page.setAudiences( page.audienceIds.map( tagId => this.getAudience( tagId ) ) );

        // search for shortpage
        const shortPage = ShortPage.fromObject( page );
        const index = this.shortPages.findIndex( shortPage => shortPage.id === page.id );
        
        // update short page entries
        if (index === -1) {
            this.shortPages.push( shortPage );
        } else {
            this.shortPages.splice( index, 1, shortPage );
        }
        
        // return new 
        return page;
    }

    /**
     * @param {string} id 
     */
    async getPage( id ) {
        const page = PageTemplate.fromObject( await networkManager.fetch( "/forumEntry", "GET", { id, topic: TOPIC } ) );
        page.setTags( page.tagIds.map( tagId => this.getTag( tagId ) ) );
        page.setAudiences( page.audienceIds.map( tagId => this.getAudience( tagId ) ) );
        return page;
    }

    /**
     * @param {number} id 
     */
    toggleTag( id ) {
        const tag = this.getTag( id );
        tag.selected = !tag.selected;
    }

    /**
     * @param {number} id 
     */
    toggleAudience( id ) {
        const tag = this.getAudience( id );
        tag.selected = !tag.selected;
    }

}